/**
 * PerGuestCodesGrid — bordered cards, one per guest+code, with consistent
 * 24px gap and `pdf-no-break` page-break safety.
 *
 * Shared between Activity and Flight vouchers so the operational layout
 * (guest name above · barcode/QR in middle · payload value below) is identical
 * across voucher types.
 *
 * Pairing rules (handled by caller, passed as `pairs`):
 *   - codes.length === names.length  → 1:1 pairing
 *   - codes.length  >  names.length  → repeat the lead guest for every code
 *     (spec: "if no other guest name is given use Guest 1 details on other
 *     Barcode / QR")
 *   - codes.length === 1             → caller renders a single strip, doesn't
 *     use this grid
 *
 * Each card carries `data-testid="per-guest-card-{i}"` for E2E targeting.
 */
import React from 'react';
import VoucherBarcodeStrip from './VoucherBarcodeStrip';

export default function PerGuestCodesGrid({ pairs, accent = '#595A5B', labelPrefix = 'Guest' }) {
  if (!pairs || pairs.length === 0) return null;
  const cols = pairs.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
  return (
    <div className={`grid gap-6 ${cols}`} data-testid="per-guest-codes">
      {pairs.map((p, i) => (
        <div
          key={i}
          className="flex flex-col items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white break-inside-avoid pdf-no-break shadow-sm"
          style={{ pageBreakInside: 'avoid' }}
          data-testid={`per-guest-card-${i}`}
        >
          <div className="w-full text-center">
            <div className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: accent }}>
              {labelPrefix} {i + 1}
            </div>
            <div
              className="mt-0.5 text-sm font-semibold text-slate-900 leading-tight break-words"
              data-testid={`per-guest-name-${i}`}
            >
              {p.name || '—'}
            </div>
            {p.subtitle && (
              <div className="mt-0.5 text-[11px] text-slate-500">{p.subtitle}</div>
            )}
          </div>

          <VoucherBarcodeStrip
            value={p.code.value}
            imageBase64={p.code.imageBase64}
            kind={p.code.kind || 'qr'}
            accent={accent}
            label={
              // Clean human label per format; raw payload kept in title attr
              // by VoucherBarcodeStrip for hover.
              ((kind) => {
                const k = (kind || '').toString().toLowerCase();
                if (k === 'qr' || k === 'qrcode' || k === 'aztec' || k === 'datamatrix') return 'QR';
                if (k === 'pdf417' || k === 'code128' || k === 'code39' || k === 'ean' || k === 'upc' || k === 'barcode') return 'BARCODE';
                return 'CODE';
              })(p.code.kind)
            }
            size="prominent"
          />
        </div>
      ))}
    </div>
  );
}
